<template>
  <div v-if="user" class="profile" redesign>
    <!-- Header -->
    <div class="local-hero">
      <h1 class="local-hero-header" v-translate>Profile</h1>
    </div>

    <div v-if="saving || loading" class="form-container">
      <LoadSpinner :color="`#${organizationDetails.darkBrandColor}`" />
    </div>

    <!-- Edit profile view -->
    <div v-if="editing && !loading && !saving" class="profile-form form-container">
      <form class="form" @submit.prevent="handleSubmit" v-if="!loading">
        <div class="row-wrapper">
          <FormTextField
            id="firstName"
            label="First name"
            :tabIndex="1"
            :value.sync="userEdited.firstName"
            :showErrorMessage="hasSubmitted"
            :errorMessage="errors && errors.firstName ? errors.firstName[0] : ''"
            required
          />
          <FormTextField
            id="lastName"
            label="Last name"
            :tabIndex="2"
            :value.sync="userEdited.lastName"
            :showErrorMessage="hasSubmitted"
            :errorMessage="errors && errors.lastName ? errors.lastName[0] : ''"
            required
          />
        </div>
        <div class="row-wrapper">
          <FormTextField
            id="phoneNumber"
            label="Phone number"
            helpText="(###)###-####"
            :tabIndex="3"
            :value.sync="userEdited.phoneNumber"
            :showErrorMessage="hasSubmitted"
            :errorMessage="errors && errors.phoneNumber ? errors.phoneNumber[0] : ''"
            :required="userEdited.enablePhoneContact || userEdited.enableSmsAlerts"
            mask="(###) ###-####"
          />
          <FormTextField
            id="zipCode"
            label="Zip code"
            helpText="#####"
            :tabIndex="4"
            :value.sync="userEdited.zipCode"
            :showErrorMessage="hasSubmitted"
            :errorMessage="
              zipCodeValid
                ? ''
                : (errors.zipCode && errors.zipCode[0]) ||
                  (clientErrors.zipCode && clientErrors.zipCode[0]) ||
                  'Invalid'
            "
            :required="employeeSchema.hasOwnProperty('zipCode')"
          />
        </div>
        <div class="row-wrapper">
          <FormTextField
            id="email"
            label="Personal email address"
            helpText="example@email.com"
            :tabIndex="5"
            :value.sync="userEdited.email"
            :showErrorMessage="hasSubmitted"
            :errorMessage="errors && errors.email ? errors.email[0] : ''"
            required
          />
          <FormTextField
            id="paypalEmail"
            label="PayPal email address"
            helpText="example@email.com"
            :tabIndex="6"
            :value.sync="userEdited.paypalEmail"
            :showErrorMessage="hasSubmitted"
            :errorMessage="errors && errors.paypalEmail ? errors.paypalEmail[0] : ''"
          />
        </div>
        <div v-if="enableLangPref" class="form-field">
          <h4 class="form-field-header" v-translate>
            What is your preferred language for communication?
          </h4>
          <div class="form-field-container">
            <div :class="{ 'md-invalid': this.errors || !userEdited.preferredLanguage }">
              <div class="radio-buttons">
                <label
                  v-for="(localeName, localeCode) in supportedLanguages"
                  :key="localeCode"
                  class="radio-button"
                >
                  <input
                    type="radio"
                    name="preferredLanguage"
                    v-model="userEdited.preferredLanguage"
                    :value="localeCode"
                  />
                  <span>{{ localeName }}</span>
                </label>
              </div>
            </div>
          </div>
          <div class="error-text">
            <span>{{
              userEdited.preferredLanguage ? '' : $gettext('Please select your preferred language')
            }}</span>
          </div>
        </div>
        <div v-if="enableSms" class="form-field">
          <h4 class="form-field-header" v-translate>
            communicationPreferences
          </h4>
          <div class="form-field-container">
            <div
              :class="{
                'md-invalid':
                  this.errors || (!userEdited.enableSmsAlerts && !userEdited.enableEmailAlerts),
              }"
            >
              <div class="checkboxes">
                <label class="checkbox">
                  <input
                    type="checkbox"
                    id="emailAlerts"
                    name="emailAlerts"
                    v-model="userEdited.enableEmailAlerts"
                  />
                  <span class="form-checkbox" />
                  <span v-translate>Email</span>
                </label>
                <label class="checkbox">
                  <input
                    type="checkbox"
                    id="smsAlerts"
                    name="smsAlerts"
                    v-model="userEdited.enableSmsAlerts"
                  />
                  <span class="form-checkbox" />
                  <span v-translate>SMS</span>
                </label>
              </div>
            </div>
          </div>
          <div class="error-text">
            <span>
              {{
                userEdited.enableSmsAlerts || userEdited.enableEmailAlerts
                  ? ''
                  : $gettext('Please select at least one of the options available')
              }}
            </span>
          </div>
        </div>
        <div v-if="enableSms" class="form-field">
          <h4 class="form-field-header" v-translate>
            Can we reach out to you by phone if we need more information from you to process your
            request?
          </h4>
          <div class="form-field-container">
            <div class="radio-buttons">
              <label class="radio-button">
                <input
                  type="radio"
                  name="enablePhoneContact"
                  v-model="userEdited.enablePhoneContact"
                  :value="true"
                />
                <span v-translate>Yes</span>
              </label>
              <label class="radio-button">
                <input
                  type="radio"
                  name="enablePhoneContact"
                  v-model="userEdited.enablePhoneContact"
                  :value="false"
                />
                <span v-translate>No</span>
              </label>
            </div>
          </div>
        </div>
      </form>
    </div>

    <!-- Default profile view -->
    <div v-if="!editing && !loading" class="profile-form form-container">
      <div class="row-wrapper">
        <div class="form-field">
          <div class="field-name" v-translate>First name</div>
          <div class="field-value">{{ user.firstName }}</div>
        </div>
        <div class="form-field">
          <div class="field-name" v-translate>Last name</div>
          <div class="field-value">{{ user.lastName }}</div>
        </div>
      </div>
      <div class="row-wrapper">
        <div class="form-field">
          <div class="field-name" v-translate>Phone number</div>
          <div v-if="user.phoneNumber !== ''" class="field-value">{{ user.phoneNumber }}</div>
          <div v-else class="field-value">N/A</div>
        </div>
        <div class="form-field">
          <div class="field-name" v-translate>Zip code</div>
          <div class="field-value">{{ user.zipCode }}</div>
        </div>
      </div>
      <div class="row-wrapper">
        <div class="form-field">
          <div class="field-name" v-translate>Personal email address</div>
          <div class="field-value">{{ user.email }}</div>
        </div>
        <div class="form-field">
          <div class="field-name" v-translate>Paypal email address</div>
          <div v-if="user.paypalEmail !== ''" class="field-value">{{ user.paypalEmail }}</div>
          <div v-else class="field-value">N/A</div>
        </div>
      </div>
      <div class="form-field">
        <div class="field-name">
          <translate :translate-params="{programName: organizationDetails.programName}">
            Preferred language for %{programName} communications
          </translate>
        </div>
        <div class="field-value">{{ supportedLanguage }}</div>
      </div>
      <div class="form-field">
        <div class="field-name" v-translate>Communication preferences</div>
        <div v-if="user.enableEmailAlerts" class="field-value" v-translate>Email</div>
        <div v-if="user.enableSmsAlerts" class="field-value" v-translate>SMS</div>
      </div>
      <div class="form-field">
        <div class="field-name" v-translate>
          Can we reach out to you by phone if we need more information from you to process your
          request?
        </div>
        <div v-if="user.enablePhoneContact || user.enableSmsAlerts" class="field-value" v-translate>
          Yes
        </div>
        <div v-else class="field-value">No</div>
      </div>
    </div>

    <!-- Edit button -->
    <div v-if="!editing" class="button-container">
      <md-button
        :disabled="saving"
        class="form-button form-button-160"
        @click="handleEdit"
        v-if="!loading"
      >
        <span v-translate>Edit</span>
      </md-button>
    </div>

    <!-- Save / cancel buttons -->
    <div v-if="editing & !saving" class="button-container">
      <button
        :disabled="saving"
        class="form-button white-button"
        @click="handleCancel"
        v-if="!loading"
      >
        <span v-translate>Cancel</span>
      </button>
      <button
        :disabled="saving"
        class="save-changes-button form-button"
        @click="handleSubmit"
        v-if="!loading"
      >
        <span v-translate>Save changes</span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Employee from '@/services/employees'
import User from '@/services/users'
import { objectToCamelCase } from '@/services/utils'
import LoadSpinner from '@/components/LoadSpinner'
import Organization from '@/services/organizations'
import FormTextField from '../components/forms/FormTextField'

export default {
  name: 'MyProfile',
  components: {
    LoadSpinner,
    FormTextField,
  },
  data() {
    return {
      user: null,
      userId: '',
      editing: false,
      loading: false,
      saving: false,
      errors: null,
      enableSms: true,
      zipCodeMask: '#####',
      clientErrors: {},
      employeeSchema: {},
      userEdited: null,
      hasSubmitted: false,
    }
  },
  async created() {
    this.loading = true
    this.userId = this.$store.state.user.id
    await Employee.api.retrieve(this.userId).then(res => {
      this.user = res
    })
    await this.getEmployeeSchema()
    this.loading = false
  },
  methods: {
    async getEmployeeSchema() {
      await Employee.api.schema().then(res => {
        this.employeeSchema = res['actions']['POST']
      })
    },
    checkValidation() {
      const isZipCodeRequiredOnOrg =
        this.employeeSchema && this.employeeSchema.hasOwnProperty('zipCode')
      const newErrors = {}
      if (!this.userEdited.firstName.length) {
        newErrors.firstName = [this.$gettext('First name may not be blank')]
        this.focusField('firstName')
      }
      if (!this.userEdited.lastName.length) {
        newErrors.lastName = [this.$gettext('Last name may not be blank')]
        this.focusField('lastName')
      }
      if (!this.userEdited.email.length) {
        newErrors.email = [this.$gettext('Email address may not be blank')]
        this.focusField('email')
      }
      if (!this.userEdited.email.includes('@') || !this.userEdited.email.includes('.')) {
        const msg = this.$gettext('Please provide a valid email address')
        newErrors.email ? newErrors.email.push(msg) : (newErrors.email = [msg])
        this.focusField('email')
      }
      if (
        this.userEdited.paypalEmail &&
        (!this.userEdited.paypalEmail.includes('@') || !this.userEdited.paypalEmail.includes('.'))
      ) {
        newErrors.paypalEmail = [this.$gettext('Please provide a valid email address')]
        this.focusField('paypalEmail')
      }
      if (isZipCodeRequiredOnOrg && !this.userEdited.zipCode) {
        newErrors.zipCode = [this.$gettext('Zip code may not be blank')]
        this.focusField('zipCode')
      }
      if (isZipCodeRequiredOnOrg && !this.zipCodeValid) {
        const msg = this.$gettext('Please provide a valid zip code')
        newErrors.zipCode ? newErrors.zipCode.push(msg) : (newErrors.zipCode = [msg])
        this.focusField('zipCode')
      }
      if (
        (this.userEdited.enablePhoneContact || this.userEdited.enableSmsAlerts) &&
        !this.userEdited.phoneNumber.length
      ) {
        newErrors.phoneNumber = [this.$gettext('Phone number may not be blank')]
        this.focusField('phoneNumber')
      }
      if (this.userEdited.phoneNumber.length > 14 || !this.phoneNumberValid) {
        const msg = this.$gettext('Please provide a valid phone number')
        newErrors.phoneNumber ? newErrors.phoneNumber.push(msg) : (newErrors.phoneNumber = [msg])
        this.focusField('phoneNumber')
      }

      if (!this.userEdited.preferredLanguage) {
        newErrors.preferredLanguage = [this.$gettext('Please select your preferred language')]
      }

      if (!this.userEdited.enableSmsAlerts && !this.userEdited.enableEmailAlerts) {
        newErrors.notifPreferences = [
          this.$gettext('Please select at least one of the options available'),
        ]
      }

      this.errors = newErrors
    },
    focusField(id) {
      document.getElementById(id).focus()
    },
    async handleSubmit() {
      this.hasSubmitted = true
      this.checkValidation()

      const hasErrors = this.errors && Object.keys(this.errors) && Object.keys(this.errors).length
      if (this.saving || hasErrors) {
        return
      }

      // Update the model from the edited version
      this.user.firstName = this.userEdited.firstName
      this.user.lastName = this.userEdited.lastName
      this.user.phoneNumber = this.userEdited.phoneNumber
      this.user.zipCode = this.userEdited.zipCode
      this.user.email = this.userEdited.email
      this.user.paypalEmail = this.userEdited.paypalEmail
      this.user.preferredLanguage = this.userEdited.preferredLanguage
      this.user.enableEmailAlerts = this.userEdited.enableEmailAlerts
      this.user.enableSmsAlerts = this.userEdited.enableSmsAlerts
      this.user.enablePhoneContact = this.userEdited.enablePhoneContact

      this.saving = true
      this.errors = null
      this.user.hasSetNotifPreferences =
        !!this.user.enableEmailAlerts || !!this.user.enableSmsAlerts

      await Employee.api
        .update(this.user)
        .then(res => {
          this.user = res
          this.$store.dispatch('updateUser', Employee.fromAPI(res))
          this.$store.commit('UPDATE_CURRENT_LANGUAGE', this.user.preferredLanguage)

          if (
            this.user.phoneNumber.length &&
            !this.user.validatedNumber &&
            this.user.enableSmsAlerts
          ) {
            this.sendCode(this.user)
          }
        })
        .catch(err => {
          this.user.id = this.userId
          this.errors = objectToCamelCase(err.response.data)
        })
      this.saving = false
      this.userEdited = null
      this.editing = false
    },
    handleEdit() {
      this.userEdited = {
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        phoneNumber: this.user.phoneNumber,
        zipCode: this.user.zipCode,
        email: this.user.email,
        paypalEmail: this.user.paypalEmail,
        preferredLanguage: this.user.preferredLanguage,
        enableEmailAlerts: this.user.enableEmailAlerts,
        enableSmsAlerts: this.user.enableSmsAlerts,
        enablePhoneContact: this.user.enablePhoneContact,
      }
      this.editing = true
    },
    handleCancel() {
      this.userEdited = null
      this.editing = false
    },
    async sendCode(user) {
      try {
        const response = await User.api.twilioChallenge(user)
        this.$router.push({
          name: 'Validation',
          params: { routeName: 'Home' },
        })
      } catch (err) {
        this.$router.push({
          name: 'Validation',
          params: {
            codeError: true,
            errorMessage: err?.response?.data?.phone_number[0],
            routeName: 'MyProfile',
          },
        })
      }
    },
  },
  watch: {
    '$language.current': async function() {
      /* TODO: Move this to a more general area.*/
      await Organization.api.getMyOrganization().then(async organization => {
        this.$store.dispatch('updateOrganizationDetails', organization)
      })
    },
  },
  computed: {
    ...mapState(['organizationDetails']),
    formattedDate() {
      const locale = this.$store.state.user.preferredLanguage.replace('_', '-') || 'en-US'
      return new Date().toLocaleDateString(locale)
    },
    supportedLanguage() {
      if(!this.supportedLanguages)
        return 'N/A'

      return this.supportedLanguages[this.user.preferredLanguage] || 'N/A'
    },
    supportedLanguages() {
      if(!this.organizationDetails)
        return null

      return this.organizationDetails.languages
    },
    enableLangPref() {
      if(!this.organizationDetails)
        return false

      return this.organizationDetails.enableLanguagePref
    },
    phoneNumberValid() {
      const phoneRegex = new RegExp(/^\(\d{3}\)\s?\d{3}\-\d{4}$/)

      if (!this.enableSms) {
        this.userEdited.phoneNumber = ''
        return true
      }

      if (
        !this.userEdited.enableSmsAlerts &&
        !this.userEdited.enablePhoneContact &&
        this.userEdited.phoneNumber.length === 0
      ) {
        return true
      }

      if (
        this.userEdited.phoneNumber.length <= 14 &&
        phoneRegex.test(this.userEdited.phoneNumber)
      ) {
        return true
      }

      return false
    },
    zipCodeValid() {
      if (!this.employeeSchema.hasOwnProperty('zipCode') && !this.userEdited.zipCode) {
        return true
      }
      const zipRegex = new RegExp(/^[0-9]{5}?$/)
      this.errors = { ...this.errors, zipCode: [] }

      if (this.userEdited.zipCode && this.userEdited.zipCode.length === 0) {
        return true
      }

      const zipValid =
        this.userEdited.zipCode &&
        this.userEdited.zipCode.length > 1 &&
        (this.userEdited.zipCode.length === 5 || this.user.zipCode.length === 10) &&
        !(this.errors && this.errors.zipCode && this.errors.zipCode.length > 0) &&
        zipRegex.test(this.userEdited.zipCode)

      if (!zipValid) {
        this.clientErrors = {
          ...this.clientErrors,
          zipCode: [this.$gettext('Please provide a valid zip code')],
        }

        return false
      } else {
        this.clientErrors = { ...this.clientErrors, zipCode: [] }

        return true
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/mixins/responsive';

.profile {
  display: flex;
  flex-direction: column;

  .profile-form {
    .row-wrapper {
      margin-top: 0;
    }

    @extend .constrain-tablet-and-up;
  }
}

.button-container {
  @include tablet-and-up-screen {
    width: 400px;
    align-self: center;
  }
}
</style>
